"use client";

import { useEffect, useRef } from "react";
import { osmAttribution, hideLeafletPrefix } from "@/lib/osm-attribution";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { deTekst } from "@/lib/de-text";

/**
 * Compact single-hotel location map for the hotel-detail modal
 * (PartnerHotelsClient). Shows ONE pin at the selected hotel's coordinates,
 * under the hotel description, so the customer sees where that specific hotel
 * lies (Dennis 2026-06-03). No legend, no region colouring — that lives in the
 * offer-page HotelLocationMap. Degrades to null when the hotel has no
 * usable latitude/longitude.
 */
function escapeHtml(s) {
  return String(s || "").replace(/[&<>"']/g, (c) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]
  ));
}

/**
 * True only when the hotel carries a usable numeric latitude + longitude.
 * Pure helper so the null-guard can be unit-tested without leaflet/jsdom.
 */
export function hasValidCoords(hotel) {
  const rawLat = hotel?.latitude;
  const rawLng = hotel?.longitude;
  // Reject null/undefined/empty BEFORE Number(), because Number(null) and
  // Number("") both coerce to 0 (a finite value), which would otherwise
  // drop a pin at lat/lng 0 ("null island") for a hotel with no coordinates.
  if (rawLat == null || rawLat === "" || rawLng == null || rawLng === "") return false;
  const lat = Number(rawLat);
  const lng = Number(rawLng);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

/**
 * Popup-tekst van de speld. `cityLabel` is de plaatsnaam zoals de adresregel
 * hem toont (bv. "Keulen" voor een hotel met plaats "Cologne"); zonder valt
 * hij terug op de plaats van het hotel.
 */
export function miniMapPopupHtml(hotel, cityLabel) {
  return `<b>${escapeHtml(hotel?.hotel_name)}</b><br>${escapeHtml(cityLabel || hotel?.city)}`;
}

export default function HotelMiniMap({ hotel, cityLabel }) {
  const mapRef = useRef(null);
  const instRef = useRef(null);
  const { locale } = useSiteContext();

  const lat = Number(hotel?.latitude);
  const lng = Number(hotel?.longitude);
  const hasCoords = hasValidCoords(hotel);
  const depKey = hasCoords ? `${lat},${lng}` : "none";

  useEffect(() => {
    if (!mapRef.current || !hasCoords) return;
    let cancelled = false;
    (async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (cancelled || !mapRef.current) return;
      if (instRef.current) {
        instRef.current.remove();
        instRef.current = null;
      }
      const map = L.map(mapRef.current, {
        scrollWheelZoom: false,
        attributionControl: true,
      });
      instRef.current = map;
      hideLeafletPrefix(map);
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: osmAttribution(locale),
        // OSM needs a Referer even on no-referrer redemption pages.
        // Send only the origin, never voucher paths or query parameters.
        referrerPolicy: "origin",
      }).addTo(map);
      const icon = L.divIcon({
        className: "",
        html: '<div style="background:#2563eb;border:2px solid #fff;border-radius:50% 50% 50% 0;transform:rotate(-45deg);width:24px;height:24px;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>',
        iconSize: [24, 24],
        iconAnchor: [12, 24],
        popupAnchor: [0, -22],
      });
      L.marker([lat, lng], { icon })
        .addTo(map)
        .bindPopup(miniMapPopupHtml(hotel, cityLabel));
      map.setView([lat, lng], 13);
    })();
    return () => {
      cancelled = true;
      if (instRef.current) {
        instRef.current.remove();
        instRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey, cityLabel]);

  if (!hasCoords) return null;

  return (
    <div className="py-2">
      <strong className="text-gray-900 text-sm sm:text-base block mb-2">
        {deTekst(locale, "sx.hotelmap.mini_title", "Locatie van dit hotel")}
      </strong>
      <div
        ref={mapRef}
        className="isolate h-[220px] w-full overflow-hidden rounded-lg border border-gray-200"
      />
    </div>
  );
}
